export const agents = [
  {
    name: "AI Receptionist",
    desc: "Answers your calls and messages day or night, books appointments, and never puts a customer on hold.",
    steps: [
      "A customer calls, texts, or messages, day or night, and never hits voicemail.",
      "A warm, friendly voice answers, just like your best front-desk person, and handles their questions about services, prices, and hours.",
      "It checks your calendar and books, moves, or cancels appointments on the spot, then texts a confirmation.",
      "Anything urgent goes straight to your team with a quick summary, so nothing slips.",
    ],
    gets: ["No missed calls", "24/7 booking", "Fewer no-shows"],
    great: "Spas, doctors' offices, real estate, home services",
    build: {
      phases: [
        { t: "Learn", d: "Your services, pricing, policies, voice, and top questions." },
        { t: "Build", d: "Phone line, custom voice, knowledge base, booking, texts, CRM, and urgent-call handoff." },
        { t: "Test", d: "Dozens of real call scenarios, tuned until it sounds like your team." },
        { t: "Launch", d: "We go live and watch the first calls closely." },
        { t: "Ongoing Support", d: "Monthly transcript reviews, updates, tuning, and new features as you grow." },
      ],
    },
  },
  {
    name: "AI Sales",
    desc: "Finds new customers, sends friendly follow-up emails, and books meetings on your calendar for you.",
    steps: [
      "We agree on who your ideal customers are.",
      "Your AI Sales helper finds good-fit prospects and writes a personal first message to each.",
      "It follows up politely on schedule and answers simple questions.",
      "When someone's interested, it books a meeting straight onto your calendar.",
    ],
    gets: ["A steady flow of new conversations", "Follow-ups that never slip", "More time for your team to close"],
    great: "Construction, tech services, B2B and professional services",
    build: {
      label: "AI Sales Agent",
      phases: [
        { t: "Learn", d: "Your offers, pricing, ideal customer, sales process, common objections, and tone." },
        { t: "Build", d: "Lead capture, instant text, email, and call follow-up, qualifying, booking, CRM pipeline, and hot-lead handoff." },
        { t: "Test", d: "Real conversations on every channel, tuned until it sounds like your best salesperson." },
        { t: "Launch", d: "We go live and watch the first conversations closely." },
        { t: "Ongoing Support", d: "Monthly reviews, script and follow-up tuning, cold-lead revival, and new features as you grow." },
      ],
    },
  },
  {
    name: "AI Response",
    desc: "Replies to your emails, reviews, and messages quickly, in your own voice, so no one is left waiting.",
    steps: [
      "We teach it your tone, your services, and your common answers.",
      "New emails, reviews, and messages are read as they arrive.",
      "It drafts or sends a friendly, accurate reply in minutes.",
      "Sensitive messages are flagged for a person to handle.",
    ],
    gets: ["Faster replies, happier customers", "Every review answered", "A cleaner inbox for your team"],
    great: "Any business with a busy inbox or lots of online reviews",
    build: {
      label: "AI Response Agent",
      phases: [
        { t: "Learn", d: "Your services, policies, tone, and the answers you give most." },
        { t: "Build", d: "Inbox, review site, and DM connections, reply library, approval rules, and escalation for sensitive messages." },
        { t: "Test", d: "Hundreds of real past messages, tuned until replies sound like you." },
        { t: "Launch", d: "We go live and check early replies closely." },
        { t: "Ongoing Support", d: "Monthly reply reviews, new answers as offers change, review-response tuning, and new channels as you grow." },
      ],
    },
  },
  {
    name: "AI Social Media",
    desc: "Creates and posts your social media content on schedule, without you lifting a finger.",
    steps: [
      "We set your brand style, topics, and posting schedule together.",
      "It creates posts, captions, and simple visuals for each platform.",
      "You can approve posts with one click, or let it post automatically.",
      "It tracks what performs best and adjusts future posts.",
    ],
    gets: ["Consistent posting without the effort", "Content that sounds like you", "Simple monthly performance recap"],
    great: "Spas, landscaping, restaurants, real estate, local shops",
    build: {
      label: "AI Social Media Agent",
      phases: [
        { t: "Learn", d: "Your brand, audience, offers, and the posts that work for you." },
        { t: "Build", d: "Content calendar, branded templates, caption style, platform connections, and one-click approvals." },
        { t: "Test", d: "A full sample month of posts, refined until it feels on-brand." },
        { t: "Launch", d: "We start posting and watch early engagement closely." },
        { t: "Ongoing Support", d: "Monthly performance reviews, fresh content themes, seasonal campaigns, and platform updates handled for you." },
      ],
    },
  },
  {
    name: "AI Customer Care",
    desc: "Welcomes new clients, answers their questions, and checks in so they keep coming back.",
    steps: [
      "A new client signs up and gets a warm welcome with everything they need.",
      "Their questions are answered quickly by email, text, or chat.",
      "It checks in after key moments, like a first visit or finished job.",
      "It spots unhappy customers early and alerts your team.",
    ],
    gets: ["Smoother onboarding", "More repeat business", "Problems caught before they grow"],
    great: "Healthcare, tech services, memberships, subscription businesses",
    build: {
      label: "AI Customer Care Agent",
      phases: [
        { t: "Learn", d: "Your onboarding steps, common questions, and what keeps clients happy." },
        { t: "Build", d: "Welcome sequences, help-desk answers, check-in timing, satisfaction alerts, and CRM connection." },
        { t: "Test", d: "Real client journeys from sign-up to repeat visit, tuned end to end." },
        { t: "Launch", d: "We go live and watch the first clients closely." },
        { t: "Ongoing Support", d: "Monthly conversation reviews, new answers as services change, retention tuning, and early-warning updates." },
      ],
    },
  },
  {
    name: "AI Hiring Assistant",
    desc: "Sorts through job applicants, answers their questions, and schedules interviews for you.",
    steps: [
      "You share the role and what a great hire looks like.",
      "It reviews every application and highlights the best matches.",
      "It answers applicant questions and keeps them updated.",
      "It books interviews with your top picks on your calendar.",
    ],
    gets: ["Hours saved on every hire", "A shortlist of strong candidates", "A better experience for applicants"],
    great: "Construction, landscaping, healthcare, growing teams",
    build: {
      label: "AI Hiring Assistant",
      phases: [
        { t: "Learn", d: "Your roles, must-haves, culture, and how you like to interview." },
        { t: "Build", d: "Job post intake, applicant screening, candidate Q&A, interview booking, and status updates." },
        { t: "Test", d: "Real past applicants run through the system, tuned until picks match yours." },
        { t: "Launch", d: "We go live on your next open role and review every shortlist." },
        { t: "Ongoing Support", d: "Monthly screening reviews, new roles added, fairness checks, and job board changes handled for you." },
      ],
    },
  },
  {
    name: "AI Sales Call Notes",
    desc: "Listens to your sales calls and gives you a short summary of what was said and what to do next.",
    steps: [
      "Your sales calls are recorded with the customer's permission.",
      "It writes a short, clear summary of each call.",
      "It lists key concerns, promises made, and next steps.",
      "Notes are saved to your customer records automatically.",
    ],
    gets: ["No more note-taking during calls", "Nothing promised gets forgotten", "Easy coaching for your team"],
    great: "Real estate, tech services, any team that sells by phone",
    build: {
      label: "AI Sales Call Notes",
      phases: [
        { t: "Learn", d: "Your sales process, products, and what a great call looks like." },
        { t: "Build", d: "Call recording, consent prompts, summaries, action items, and automatic CRM notes." },
        { t: "Test", d: "Real recorded calls, tuned until summaries match what matters to you." },
        { t: "Launch", d: "We go live and check the first summaries closely." },
        { t: "Ongoing Support", d: "Monthly accuracy reviews, new talking points and products, coaching insights, and tool updates." },
      ],
    },
  },
  {
    name: "AI Website Builder",
    desc: "Builds you a good-looking website that turns visitors into customers.",
    steps: [
      "We learn about your business, customers, and style.",
      "It designs and writes a modern website tailored to you.",
      "We connect booking, contact forms, and your AI helpers.",
      "It keeps improving pages based on what visitors do.",
    ],
    gets: ["A professional site, fast", "More enquiries and bookings", "Easy updates without a developer"],
    great: "New businesses, spas, contractors, local services",
    build: {
      label: "AI Website",
      phases: [
        { t: "Learn", d: "Your business, customers, style, and what you want visitors to do." },
        { t: "Build", d: "Design, copy, booking and contact forms, AI chat, SEO basics, and analytics." },
        { t: "Test", d: "Every page on phone and desktop, every form and booking checked." },
        { t: "Launch", d: "We go live and watch early visitor behavior closely." },
        { t: "Ongoing Support", d: "Monthly updates, new pages and offers, speed and security upkeep, and conversion tuning." },
      ],
    },
  },
  {
    name: "AI Finance Reports",
    desc: "Turns your numbers into simple, easy-to-read reports so you always know where your money stands.",
    steps: [
      "We connect your accounting, sales, and banking tools.",
      "It gathers and tidies up your numbers automatically.",
      "You get simple weekly or monthly reports in plain English.",
      "It flags anything unusual, like late payments or rising costs.",
    ],
    gets: ["A clear picture of your money", "No more spreadsheet wrangling", "Early warning on cash flow"],
    great: "Any business owner who wants clearer numbers",
    build: {
      label: "AI Finance Reports",
      phases: [
        { t: "Learn", d: "Your accounts, revenue streams, costs, and the numbers you care about." },
        { t: "Build", d: "Accounting, bank, and sales connections, clean categories, plain-English reports, and alerts." },
        { t: "Test", d: "Past months rebuilt and checked against your books until they match." },
        { t: "Launch", d: "Your first live report lands, and we walk you through it." },
        { t: "Ongoing Support", d: "Monthly accuracy checks, new accounts and categories, cash-flow alerts tuning, and tool updates." },
      ],
    },
  },
];
